/**
 * screens/voice.tsx — SK Voice (POOL, read-only). SUKI answers up to N calls at
 * once (concurrency meter); overflow waits in a FIFO hold-line; escalations route
 * to a human-agent pool. Stat strip + Handling-now lane + On-hold + Human handoff.
 * Port of the prototype's Voice surface + _voiceVM.
 */

import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useQueue } from '../queue/store';
import { fmtDur } from '../queue/format';
import { Mono, TierPill } from '../queue/components/atoms';
import { useResponsive } from '../lib/responsive';
import { SurfaceFill } from '../lib/surfaceLayout';
import { useDataSource } from '../queue/dataSource';
import { useActiveQueue } from '../queue/activeQueue';
import { useVoiceMonitor } from '../screens/hooks/useMonitor';
import type { VoiceState } from '../queue/types';

export default function VoiceScreen() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { width } = useResponsive();
  const { state } = useQueue();
  const { isLiveSurface } = useDataSource();
  const { activeQueueId } = useActiveQueue();
  // Phone: stacks + page-scroll. Wide: fill the viewport, lanes scroll internally.
  const narrow = width < 820;

  // Live when wired + a queue is selected; else the in-memory demo (unchanged).
  const useLive = isLiveSurface('voice') && !!activeQueueId;
  const { view } = useVoiceMonitor(useLive ? activeQueueId : null);
  const now = Date.now();
  // Map the (thinner, privacy-safe) live monitor into the screen's view shape.
  // The live model exposes tokens not names, and pool units not named agents.
  const liveV: VoiceState | null = useLive && view ? {
    maxParallel: view.concurrency.max ?? view.handlingNow.length,
    asa: `${Math.round(view.stats.asaSeconds)}s`,
    abandoned: view.stats.abandoned,
    sl: 0,
    escalatedToday: view.stats.escalated,
    calls: view.handlingNow.map((c) => ({
      token: c.token, name: `#${c.token}`, phone: '', intent: String(c.intent ?? ''),
      lang: String(c.language ?? ''), startAt: now - c.durationSeconds * 1000,
      escalating: !!c.handingOff, feeds: false,
    })),
    hold: view.onHoldFifo.map((h) => ({
      token: h.token, name: `#${h.token}`, phone: '', startAt: now - h.holdSeconds * 1000,
      deflect: h.callbackOffered,
    })),
    agents: view.humanHandoffLane.map((u) => ({
      // rev 5 dual-state: presence is operational (ONLINE/BREAK/OFFLINE); "on call"
      // is derived from load (at capacity), not a presence value.
      name: u.label,
      status:
        u.presence !== 'ONLINE' ? 'wrap-up' : u.load >= u.capacity ? 'on-call' : 'available',
      forName: null, reason: `${u.load}/${u.capacity}`, startAt: null,
    })),
  } : null;

  const v = liveV ?? state.voice;
  const dur = (t: number) => fmtDur((now - t) / 1000);

  const stats = [
    { k: 'In handling', v: v.calls.length + '/' + v.maxParallel },
    { k: 'On hold', v: String(v.hold.length) },
    { k: 'Longest wait', v: v.hold.length ? dur(Math.min(...v.hold.map(h => h.startAt))) : '0:00' },
    { k: 'Avg answer', v: v.asa },
    { k: 'Abandoned', v: String(v.abandoned) },
    { k: '→ Human today', v: String(v.escalatedToday) },
  ];

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden' as const };
  const feedsChip = { flexDirection: 'row' as const, alignItems: 'center' as const, height: 18, paddingHorizontal: 7, borderRadius: 5, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card };
  const escChip = { flexDirection: 'row' as const, alignItems: 'center' as const, height: 18, paddingHorizontal: 7, borderRadius: 5, backgroundColor: colors.ink };

  const scrollRegion = narrow ? { maxHeight: 430 } : { flex: 1, minHeight: 0 as const };
  const panelsRow = narrow
    ? { flexDirection: 'column' as const, gap: 16 }
    : { flexDirection: 'row' as const, gap: 16, flex: 1, minHeight: 0 as const, alignItems: 'stretch' as const };

  const header = (
        <>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18 }}>
          <View style={{ flex: 1 }}>
            <Mono style={{ fontSize: 11, letterSpacing: 1.5, color: colors.ink3, textTransform: 'uppercase' }}>Client Admin · SK Voice</Mono>
            <Text style={{ fontSize: 26, fontWeight: '700', marginTop: 4, color: colors.ink }}>Voice queue</Text>
            <Text style={{ fontSize: 13.5, color: colors.ink3, marginTop: 4 }}>
              POOL · SUKI answers up to <Text style={{ fontWeight: '700', color: colors.ink }}>{v.maxParallel}</Text> calls at once. When full, callers wait in a fair line; escalations route to human agents.
            </Text>
          </View>
          <TierPill label="TIER 3 · READ-ONLY" />
        </View>

        {/* stats */}
        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {stats.map(s => (
            <View key={s.k} style={{ flex: 1, minWidth: 130, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 13 }}>
              <Mono style={{ fontSize: 21, fontWeight: '600', lineHeight: 21, color: colors.ink }}>{s.v}</Mono>
              <Text style={{ fontSize: 10, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 5 }}>{s.k}</Text>
            </View>
          ))}
        </View>

        {/* concurrency meter */}
        <View style={[card, { padding: 18, marginBottom: 16, borderRadius: 14 }]}>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 11 }}>
            <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>Concurrency</Text>
            <Text style={{ fontSize: 12.5, color: colors.ink4 }}>
              <Mono style={{ color: colors.ink, fontWeight: '600', fontSize: 14 }}>{v.calls.length}/{v.maxParallel}</Mono> call slots in use
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 5 }}>
            {Array.from({ length: v.maxParallel }).map((_, i) => (
              <View key={i} style={{
                flex: 1, height: 26, borderRadius: 5,
                backgroundColor: i < v.calls.length ? colors.ink : colors.card,
                borderWidth: i < v.calls.length ? 0 : 1, borderColor: '#D8D8D4',
              }} />
            ))}
          </View>
        </View>
        </>
  );

  const panels = (
        <View style={panelsRow}>
          {/* handling now */}
          <View style={[card, narrow ? { minWidth: 280 } : { flex: 1.5, minWidth: 280 }]}>
            <LaneHead colors={colors} title="Handling now · SUKI" note="bot · intake only" />
            <View style={scrollRegion}>
              <ScrollView>
                {v.calls.map(c => (
                  <View key={c.token} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F0EC', backgroundColor: c.escalating ? '#FBFAF6' : colors.card }}>
                    <Mono style={{ fontSize: 11, color: colors.ink4, width: 34 }}>#{c.token}</Mono>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{c.name}</Text>
                        <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
                          <Mono style={{ fontSize: 10, color: colors.ink4 }}>{c.lang}</Mono>
                        </View>
                        {c.escalating && <View style={escChip}><Mono style={{ fontSize: 10, fontWeight: '600', color: colors.white }}>handing off →</Mono></View>}
                        {c.feeds && <View style={feedsChip}><Mono style={{ fontSize: 10, fontWeight: '600', color: colors.ink3 }}>→ Restaurant line</Mono></View>}
                      </View>
                      <Text style={{ fontSize: 12, color: colors.ink4, marginTop: 2 }}>{c.intent}</Text>
                    </View>
                    <Mono style={{ fontSize: 13, fontWeight: '600', color: colors.ink2 }}>{dur(c.startAt)}</Mono>
                  </View>
                ))}
              </ScrollView>
            </View>
          </View>

          {/* hold + handoff — two stacked cards; scroll together if they overflow */}
          <View style={narrow ? { minWidth: 280 } : { flex: 1, minWidth: 280, minHeight: 0 }}>
           <ScrollView style={narrow ? undefined : { flex: 1 }} contentContainerStyle={{ gap: 16 }}>
            <View style={card}>
              <LaneHead colors={colors} title="On hold · FIFO" note="you're next when a slot frees" />
              {v.hold.length === 0 ? (
                <Text style={{ padding: 28, textAlign: 'center', color: colors.ink4, fontSize: 13 }}>No callers waiting</Text>
              ) : v.hold.map((h, i) => (
                <View key={h.token} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F0EC' }}>
                  <Mono style={{ fontSize: 14, fontWeight: '600', color: colors.ink3, width: 24 }}>{i + 1}</Mono>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{h.name}</Text>
                    <Text style={{ fontSize: 11, color: colors.ink4 }}>#{h.token} · holding {dur(h.startAt)}</Text>
                  </View>
                  {h.deflect && (
                    <View style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: colors.lineStrong, borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2 }}>
                      <Mono style={{ fontSize: 10, color: colors.ink3 }}>callback offered</Mono>
                    </View>
                  )}
                </View>
              ))}
            </View>

            <View style={card}>
              <LaneHead colors={colors} title="Human handoff" note="least-active routing" />
              {v.agents.map(a => {
                const on = a.status === 'on-call';
                const dotStyle = on
                  ? { backgroundColor: colors.ink }
                  : (a.status === 'wrap-up' ? { backgroundColor: colors.card, borderWidth: 2, borderColor: colors.ink3 } : { backgroundColor: colors.card, borderWidth: 2, borderColor: colors.lineStrong });
                const statusLabel = on ? 'On call' : (a.status === 'wrap-up' ? 'Wrap-up' : 'Available');
                return (
                  <View key={a.name} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: '#F1F0EC' }}>
                    <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: colors.paper3, alignItems: 'center', justifyContent: 'center' }}>
                      <Text style={{ fontSize: 12, fontWeight: '600', color: colors.ink2 }}>{a.name.split(' ').map(w => w[0]).slice(0, 2).join('')}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                        <View style={{ width: 9, height: 9, borderRadius: 5, ...dotStyle }} />
                        <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{a.name}</Text>
                      </View>
                      {on && <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 2 }}>{a.reason} · {a.forName} · {a.startAt ? dur(a.startAt) : ''}</Text>}
                    </View>
                    <Mono style={{ fontSize: 11, color: colors.ink3 }}>{statusLabel}</Mono>
                  </View>
                );
              })}
            </View>
           </ScrollView>
          </View>
        </View>
  );

  if (narrow) {
    return (
      <SurfaceFill>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 24 }}>
          {header}
          {panels}
        </ScrollView>
      </SurfaceFill>
    );
  }
  return (
    <SurfaceFill>
      {header}
      {panels}
    </SurfaceFill>
  );
}

function LaneHead({ colors, title, note }: { colors: any; title: string; note: string }) {
  return (
    <View style={{ paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
      <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>{title}</Text>
      <Mono style={{ fontSize: 11, color: colors.ink4 }}>{note}</Mono>
    </View>
  );
}
